import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { HoverTip, TIP_BADGE } from './HoverTip';
import { ImageLightbox } from './ImageLightbox';
import { BoxOverlay, type DrawnBox } from './BoxOverlay';
import { modelLabel } from './models';
import { CALL_TEXT } from './ReasoningBadge';
import { useLanguage } from '../../contexts/LanguageContext';

// ONE PICTURE OF A CALL, and what it is a picture OF.
//
// The caption is optional because for most of these badges there is nothing to say: a
// manual's drawing zoomed large is a drawing of the part you were already looking at. It
// earns itself where the picture cannot say what it is — six renders of one mesh are six
// brown shapes on white, and which mesh and which side is exactly what somebody opening one
// large wants to know.
// The caption is the SHORT one, and it is a different question from `label`: not "what am
// I looking at" but "which one of these is this" — FRONT, BACK, A, B. It goes under the
// tile, where a strip of eight small pictures otherwise says nothing about which is which,
// and the prompt above refers to them by exactly these names.
//
// AND `off` IS WHETHER IT ACTUALLY GOES. Present only on a picture whose inclusion is a
// question — which is what makes the caption a toggle rather than a word, given the panel
// has somewhere to write the answer (see `onTogglePicture`). Absent means the picture is
// part of the call and not up for discussion: the six renders of a mesh are the QUESTION,
// and one of them left out is a question about five sides.
//
// AND `group` IS WHOSE PICTURE IT IS, where a call's pictures belong to several things — a part
// each, in the materials call. Consecutive pictures of one group share a row under the group's
// name, centred, and the next group starts a row of its own. Wrapping onto a second row happens
// only when one group's pictures do not fit on one.
// THE INSET a call's WORDS sit on inside its window — the prompt, and the reasoning and
// output under it: one each, so the question and the answer read as two documents.
export const CALL_INSET =
  'rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-800';

export type TipPicture = { url: string; label?: string; caption?: string; off?: boolean; group?: string };

// THE CALL A BUTTON WOULD SEND: its pictures, its text, and which model reads them.
//
// Every phase here is a prompt with a button on it, and the thing you want when an answer
// looks wrong is not the answer again — it is what was asked. So this is not a nicety: it is
// the only place the app says what it is actually doing, in the words it does it in.
//
// Fetched ONCE, when somebody first asks to see it. Building a prompt costs a round trip and
// the overwhelming majority of these are never opened — none of them are worth a request on a
// page that is only being scrolled past.
//
// The prompt comes from the SERVER wherever the server builds it, even where the client
// could assemble one. A preview written twice would be right on the day it was written and
// wrong the first time somebody edited the real one, and a preview that has drifted from the
// call is worse than no preview at all.
//
// IT IS A HOOK BECAUSE IT HAS TWO FACES NOW. The same call is read on hover from a badge in a
// table and from a modal opened off a menu (see CallBadge) — same fetching, same caching,
// same refetch when what it describes changes, and it must not be written twice.
export type PromptCall = {
  prompt: string;
  pictures: TipPicture[];
  // WHERE THE SECOND GROUP STARTS in that list — the index of the first picture that came
  // from `more` rather than from `load`, and 0 where there is no first group.
  //
  // The two are different KINDS of picture, not a long run of one: photographs of the
  // assembled product, then renders of the mesh being asked about. Run together in one
  // wrapping row they read as eight of a thing, and where the wrap happens to fall — three
  // renders on the end of the photographs, three below — it reads as a grouping that means
  // something. It does not. So the panel breaks the row here. See the strip below.
  breakAt: number;
  model: string;
  loadingMore: boolean;
  // WHETHER `load` IS STILL ON ITS WAY — the words and their own pictures (see PromptPanel.pending).
  loading: boolean;
  // Ask for it, if it has not been asked for already. Cheap to call on every hover and every
  // menu opening; it is the "already have it" test.
  open: () => void;
};

export function usePromptCall({ load, more, revision }: {
  // The call: the prompt, the pictures that go with it, and WHICH MODEL it would go to. An
  // empty `urls` is how a call says it looks at nothing — the panel then draws text alone
  // rather than a gap.
  //
  // The model comes with the rest for the same reason the text does: it is part of what would
  // happen if the button were pressed, and it is not the same everywhere. Phases 1-2 run on
  // RUN_MODEL, the mesh calls choose their own on the server, and reading a prompt without
  // knowing which model reads it is reading half the question.
  load: () => Promise<{ prompt: string; urls?: (string | TipPicture)[]; model?: string }>;
  // THE PICTURES THAT COST SOMETHING, when a call has any: fetched beside `load` and appended
  // to its own, in the order the prompt numbers them.
  //
  // For one call here the images are not lying in a table — they are six Blender renders of
  // the mesh, a cold process and a 300,000-triangle import — and asking for them in `load`
  // held the whole panel on "Building the prompt…" for several seconds to show text that was
  // ready immediately. The words do not depend on them: what the prompt says about the
  // pictures is their order, and that is a constant on both sides of the wire.
  more?: () => Promise<TipPicture[]>;
  // WHAT THE ANSWER DEPENDS ON, as one string. When it changes, whatever was loaded is stale
  // and is fetched again.
  //
  // Striking a mask through is the case: the prompt SAYS which photographs carry an outline
  // ("seen from the front, with this part outlined in green") and so does the caption under
  // each one, and both of those are the server's words about a call that has just changed. A
  // panel that goes on showing them describes a call nobody would now make — which is the one
  // thing a preview must never do.
  revision?: string;
}): PromptCall {
  const { t } = useLanguage();
  const [prompt, setPrompt] = useState('');
  // THE CALL'S OWN PICTURES AND THE EXPENSIVE ONES, HELD APART. They are one strip on screen
  // and they arrive from two places (`load` and `more`), and the difference matters exactly
  // once: a refetch re-answers `load` and must not take `more`'s with it.
  //
  // As one list it did. Striking a crop through — or now leaving a photograph out — refetches
  // the words, which replaced the whole list with the photographs alone, and the six renders
  // of the mesh disappeared from a panel that was still going to send them. Which reads as
  // the call having changed shape, at the exact moment somebody is checking what it is.
  const [own, setOwn] = useState<TipPicture[]>([]);
  const [extra, setExtra] = useState<TipPicture[]>([]);
  const pictures = useMemo(() => [...own, ...extra], [own, extra]);
  const [model, setModel] = useState('');
  // Whether the second half is still coming. Only ever true where `more` was given, so a call
  // without one behaves exactly as it did before.
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // The words and the captions, from the server. Held as a callback so the effect below and
  // the pointer can ask for the same thing.
  // THE LAST ONE ASKED FOR IS THE ONE SHOWN, and the others are dropped where they land. A
  // `load` can take a moment — a step's pages are rasterised from the PDF — and two presses of
  // the panel's → start two of them, which come back in whatever order they come back in. The
  // slower one landing second overwrites the newer answer with an older question, and the
  // panel then describes a call nobody is about to make: the same failure `revision` exists to
  // prevent, arriving by the other road.
  const asked = useRef(0);
  const ask = useCallback(() => {
    const mine = (asked.current += 1);
    setLoading(true);
    void load()
      .then(({ prompt: p, urls, model: m }) => {
        if (asked.current !== mine) return;
        setLoading(false);
        setPrompt(p);
        // A bare address and a captioned picture are the same thing to everything below, so a
        // caller with nothing to say goes on passing strings.
        setOwn((urls ?? []).map((u) => (typeof u === 'string' ? { url: u } : u)));
        setModel(m ?? '');
      })
      .catch(() => {
        if (asked.current !== mine) return;
        setLoading(false);
        setPrompt(t('notice.parts.promptFailed'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, t]);

  // AND AGAIN WHEN WHAT IT DESCRIBES CHANGES — but only once something has been shown. A
  // badge nobody has opened has nothing to bring up to date, and fetching for one would spend
  // a request on every one of these on the page.
  const shown = useRef(false);
  shown.current = shown.current || !!prompt;
  useEffect(() => {
    if (shown.current) ask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  const open = useCallback(() => {
    if (prompt) return;
    ask();
    // SIDE BY SIDE with the text and not after it: the two answers are independent, and
    // waiting for the words before starting a Blender would put back the round trip that
    // splitting them took out. Appended when it lands, so the pictures stay in the order the
    // prompt numbers them.
    if (more && !loadingMore) {
      setLoadingMore(true);
      void more()
        .then(setExtra)
        // Quiet: the question is on screen and readable, and a panel that replaced it with
        // "the renders failed" would take away the half that worked to report the half that
        // did not.
        .catch(() => {})
        .finally(() => setLoadingMore(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt, ask, more, loadingMore]);

  return { prompt, pictures, breakAt: own.length, model, loadingMore, loading, open };
}

// WHAT A CALL LOOKS LIKE ON SCREEN: the model, the heading, the pictures, the words.
//
// Its own component because it is read in two places — a hover panel and a modal — and a
// second copy of this layout is a second thing to keep in step with the first. It owns the
// zoom too: the lightbox belongs to the pictures, not to whatever is showing them.
export function PromptPanel({ label, call, pending, boxesFor, onToggleBox, onTogglePicture, picker }: {
  label: string;
  call: PromptCall;
  // HOW MANY PICTURES ARE COMING, where the caller knows, while `load` builds them: a spinning tile
  // each, where they will land. A call whose pictures are drawn as it is built (Find holes renders
  // its parts in the browser, a few seconds) otherwise stood as "Building the prompt…" over nothing,
  // and the strip appeared all at once.
  pending?: number;
  // WHAT IS DRAWN ON A PICTURE, ASKED FOR EVERY TIME IT IS DRAWN. The crops of a part on a
  // photograph of the product: outlined while they count, hatched and grey once somebody has
  // struck one through.
  //
  // A FUNCTION AND NOT A LIST, which is the whole of why this works. The pictures are fetched
  // once and kept — that is what makes a call cheap — and boxes captured at that moment would
  // go on showing the state of the crops when the panel first opened. The one thing this has
  // to do is repaint the instant one is clicked.
  boxesFor?: (url: string) => DrawnBox[] | undefined;
  // Struck through here is struck through everywhere: the toggle writes the same flag the
  // parts table writes, and a struck crop is not merely dimmed — it is left out of what the
  // call sends.
  onToggleBox?: (url: string, index: number) => void;
  // AND THE WHOLE PICTURE IN OR OUT, which is the same kind of judgement one level up: a
  // photograph the part cannot be made out in is not weak evidence, it is a picture of
  // something else, and a model given it will match a render to it anyway.
  //
  // The caption is the control, because the caption is already the picture's name in the
  // prompt's own words — "FRONT", "BACK" — and a separate cross beside it would be a second
  // thing to explain. Offered only on a picture that says whether it is off (see TipPicture),
  // so the pictures that ARE the question keep a plain word under them.
  onTogglePicture?: (url: string) => void;
  // THE PICTURES AS SOMETHING TO CHOOSE, where the call is the next press of a generator: its
  // photographs in their own picker, where the strip would show them, so they are picked beside
  // the words they change (see MeshGenerateControls). The strip then holds what it does not.
  picker?: ReactNode;
}) {
  const { t } = useLanguage();
  const { prompt, pictures, breakAt, model, loadingMore, loading } = call;
  const waiting = loading && !pictures.length ? pending ?? 0 : 0;
  const [zoom, setZoom] = useState<string | null>(null);
  // The one that is open large, if any — it carries its own caption and its own boxes.
  const open = pictures.find((p) => p.url === zoom);

  return (
    <>
      {/* THE FULL WIDTH OF WHATEVER HOLDS IT. The container is a fixed 52rem so a prompt has
          a stable measure — it must not resize as the pictures land behind the text — and
          that IS the cap: there is no unbounded line here whatever the screen.

          It carried a second cap of 36rem inside that, on the argument that a monospace line
          twice 85 characters is scanned rather than read. Which is true of prose and false of
          this: a prompt is read in blocks, looking for the paragraph that says a thing, and
          the narrow column bought shorter lines by making the page half again as long and
          leaving a third of the window empty on either side. */}
      <div className="w-full">
        {/* WHICH MODEL READS ALL THIS, at the very top. A prompt is only half a question —
            the same words go to a flash model and to a reasoning one and come back different
            — and which one it is was written nowhere on the page. */}
        {model && modelLabel(model) && (
          <p className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-blue-600">
            {modelLabel(model)}
          </p>
        )}
        {/* WHAT THIS IS, AND HOW LONG IT IS — over everything it is the title of. It used to
            sit between the pictures and the text, which made it a caption for the wall of
            words and left the strip above it unlabelled: the two halves of one call read as
            two things stacked.

            THE LENGTH IN WORDS, because a prompt is read here as a wall of text and the one
            thing the wall does not say is its own size — the number worth having while
            judging what to cut. Words rather than characters: 11039 is a figure nobody has a
            feel for and 1800 words is a length everybody does. Not tokens either, which
            would be an estimate of somebody else's arithmetic. Drawn only once it has
            loaded, so a panel still fetching does not flash a "(0)". */}
        <p className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {label}
          {prompt && (
            <span className="ml-1 normal-case">
              ({prompt.trim().split(/\s+/).length} words)
            </span>
          )}
        </p>
        {/* The photographs, INSIDE the prompt, because that is what they are: the images of
            the call, above the text of it.

            These are product photographs rather than dimension drawings, so no white plate
            under them: a chair shot on a grey studio background is what it is, and forcing
            white behind it would only bleach the picture's own edges. */}
        {/* MIDDLE-ALIGNED, not bottom. The tiles are different heights — a portrait page beside
            a square render — and hung from their bottom edge the strip read as a row of things
            standing on a shelf that is not there, with the tall one's top off on its own.
            Centred, the pictures share an axis and the captions line up with each other. */}
        {picker && <div className="mb-2">{picker}</div>}
        {(pictures.length > 0 || loadingMore || waiting > 0) && (
          <div className="mb-2 flex flex-wrap items-center justify-center gap-1.5">
            {pictures.map((p2, at) => {
              const boxes = boxesFor?.(p2.url);
              // Whether this one is a picture whose inclusion is answerable, and whether
              // there is anywhere to write the answer. Both, or the caption is a word.
              const togglable = !!onTogglePicture && p2.off !== undefined;
              const tile = (
                <img
                  src={p2.url}
                  alt=""
                  title={p2.label}
                  loading="lazy"
                  onClick={() => setZoom(p2.url)}
                  // LEFT OUT IS DRAWN AS LEFT OUT. Greyed and faded, the same reading a
                  // struck-through crop gets on the tile under it — the picture stays in the
                  // strip because that is the only place it can be put back, and it must not
                  // look like one of the ones going up.
                  style={p2.off ? { filter: 'grayscale(1)', opacity: 0.4 } : undefined}
                  // SIZED BY ITS OWN ASPECT, not letterboxed inside a square. A box drawn on
                  // a picture is positioned in the picture's own 0..1 space, and BoxOverlay
                  // fills its PARENT — so an `object-contain` image in a square tile puts
                  // every mask off the photograph by the height of the letterbox. One fixed
                  // dimension and the other free means the element IS the painted image,
                  // which is the only arrangement where the two agree.
                  className="h-24 w-auto cursor-zoom-in rounded-md border border-slate-200 transition-opacity hover:opacity-80"
                />
              );
              // WHAT IS DRAWN ON IT, at tile size too. The zoom had the masks and the strip
              // did not, so the panel's own answer to "what is this call looking at" was a
              // row of chairs with nothing said about which part they are about — and the one
              // thing worth checking at a glance is whether the right thing is outlined on
              // all four.
              //
              // NO VEIL, like every other strip of these in the app: the dim outside a box is
              // that box's own shadow and does not compose, so a picture with one mask would
              // come out darkened beside one with two.
              return (
                <Fragment key={p2.url}>
                  {/* A NEW ROW WHERE THE PICTURES STOP BEING ONE KIND AND START BEING
                      ANOTHER: photographs of the assembled product above, renders of the mesh
                      below. See PromptCall.breakAt.

                      An empty item at full basis, which is how a wrapping flex row is broken
                      from the inside. The alternative is two containers, and that costs the
                      one property this strip depends on — every tile is a child of the same
                      row, so they share a baseline and the gap between a photograph and a
                      render is the gap between two photographs. Two rows would be two
                      alignments that agree until a picture of an odd height arrives.

                      Nothing is drawn: no rule, no heading. The break IS the statement, and
                      the captions under the tiles already say which kind each one is. */}
                  {at > 0 && at === breakAt && <span className="basis-full" />}
                  {/* A GROUP'S NAME, over its own row. Full basis, so it is also the break. */}
                  {p2.group && p2.group !== pictures[at - 1]?.group && (
                    <span className={`basis-full text-center text-xs font-semibold uppercase tracking-wide text-slate-500 ${at > 0 ? 'mt-2' : ''}`}>
                      {p2.group}
                    </span>
                  )}
                  <span className="flex flex-col items-center gap-1">
                    {boxes?.length ? (
                      <span className="relative inline-block leading-none">
                        {tile}
                        {boxes.map((b, k) => (
                          <BoxOverlay
                            key={k}
                            box={b.box}
                            outlines={b.outlines}
                            tone={b.tone}
                            rounded="rounded"
                            off={b.off}
                            veil={false}
                          />
                        ))}
                      </span>
                    ) : <span className="leading-none">{tile}</span>}
                    {/* WHICH ONE THIS IS, in the prompt's own words. The text above says
                        "PICTURE 1: … seen from the front" and "VIEW C", and without this the
                        strip under it is eight chairs nobody can match a line to.

                        AND, WHERE THE PICTURE IS OPTIONAL, THE BUTTON THAT TAKES IT OUT. The
                        name and the switch are one thing on purpose: what a picture is called
                        here IS its place in the prompt, so pressing the name is pressing the
                        line of the question it stands for. Struck through when it is out,
                        which is the mark a crop already wears one level down. */}
                    {p2.caption && (togglable ? (
                      <button
                        type="button"
                        onClick={() => onTogglePicture?.(p2.url)}
                        title={t(p2.off ? 'notice.tip.picInclude' : 'notice.tip.picExclude')}
                        aria-pressed={!p2.off}
                        className={`rounded px-1 text-center text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                          p2.off
                            ? 'text-slate-500 line-through hover:text-slate-700'
                            : 'text-blue-600 hover:text-blue-700'
                        }`}
                      >
                        {p2.caption}
                      </button>
                    ) : (
                      // GREEN, AND AS MANY LINES AS IT WAS WRITTEN IN. A caption that is not
                      // a toggle used to be grey and one long line — "SPRING WASHER (F) — FRONT
                      // VIEW" set across a 96px tile, which either overflows it or shrinks the
                      // strip to fit the longest name in it. `pre-line` keeps the newlines the
                      // caller wrote, so a caption that is two facts is two rows; the green is
                      // the one the togglable captions already wear, and the difference between
                      // them was never the colour but whether pressing does anything.
                      <span className="whitespace-pre-line text-center text-[10px] font-semibold uppercase leading-tight tracking-wide text-blue-600">
                        {p2.caption}
                      </span>
                    ))}
                  </span>
                </Fragment>
              );
            })}
            {/* THE ONES STILL BEING MADE, drawn as the tiles they will be rather than as a
                spinner somewhere else. The panel keeps its shape as they land, and a gap
                where a picture is coming says what a message would.

                ON THE ROW THEY WILL LAND ON. These are always `more`'s pictures — the second
                group — so the break above belongs in front of them too, or the strip shuffles
                up a line at the moment they arrive. */}
            {loadingMore && pictures.length > 0 && <span className="basis-full" />}
            {[...Array(loadingMore ? 1 : waiting)].map((_, k) => (
              <span key={k} className="flex h-24 w-24 items-center justify-center rounded-md border border-dashed border-slate-500">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              </span>
            ))}
          </div>
        )}
        <pre className={`${CALL_TEXT} ${CALL_INSET}`}>
          {prompt || t('notice.parts.promptLoading')}
        </pre>
      </div>

      {/* WHAT IT IS A PICTURE OF, under the picture — which turns the zoom from a bare image
          on black into the panel the rest of this app zooms into (see ImageLightbox, where
          `details` is what draws the card and the matte round it).
          Only where there is something to say. Six renders of one mesh are six brown shapes
          on white and the arrows step between them, so "which mesh, which side" is the whole
          of what somebody who opened one large is asking. A drawing of a part is already a
          picture of the part, and a caption saying so is a caption. */}
      <ImageLightbox
        src={zoom}
        srcs={pictures.map((p2) => p2.url)}
        boxes={zoom ? boxesFor?.(zoom) : undefined}
        onToggleBox={zoom && onToggleBox ? ((at) => onToggleBox(zoom, at)) : undefined}
        details={open?.label
          ? <p className="text-center text-xs text-slate-500">{open.label}</p>
          : undefined}
        onNavigate={setZoom}
        onClose={() => setZoom(null)}
      />
    </>
  );
}

// THE QUESTION A BUTTON WOULD SEND, on hover.
//
// The badge for a call that stands ALONE — a column header, a table's one estimate, a row in
// the parts list. Where a call also has an answer to show, the two are one badge and a menu
// instead: see CallBadge, which is this panel and the answer's, opened from one icon.
export function PromptBadge({
  label, load, more, boxesFor, onToggleBox, onTogglePicture, revision,
}: {
  label: string;
  load: () => Promise<{ prompt: string; urls?: (string | TipPicture)[]; model?: string }>;
  more?: () => Promise<TipPicture[]>;
  boxesFor?: (url: string) => DrawnBox[] | undefined;
  onToggleBox?: (url: string, index: number) => void;
  onTogglePicture?: (url: string) => void;
  revision?: string;
}) {
  const call = usePromptCall({ load, more, revision });
  return (
    <HoverTip
      // The prompt alone. The panel also draws the call's pictures and a caption naming the
      // question, and neither is any use in a paste: what you do with this is put it in front
      // of another model, or in a message asking why it answered that.
      copy={call.prompt}
      trigger={
        <span className={TIP_BADGE} title={label} onMouseEnter={call.open}>
          <FileText className="h-3.5 w-3.5" />
        </span>
      }
      content={(
        <PromptPanel
          label={label}
          call={call}
          boxesFor={boxesFor}
          onToggleBox={onToggleBox}
          onTogglePicture={onTogglePicture}
        />
      )}
    />
  );
}
